import React from 'react';
import { Link } from 'react-router-dom';

const ShippingPolicy = () => {
    return (
        <section className="shipping-policy-section py-80 bg-gray-50 position-relative">
            <style>{`
                .policy-card {
                    background: #ffffff;
                    border: 1px solid #EFEFEF;
                    border-radius: 16px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03);
                    transition: all 0.3s ease;
                }
                .policy-card:hover {
                    box-shadow: 0 8px 30px rgba(0,0,0,0.06);
                }
                .policy-icon-box {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    background: #EAF8EE;
                    color: var(--main-600, #2FA84F);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    flex-shrink: 0;
                }
                .policy-notice-box {
                    background: #F8FDF9;
                    border-left: 4px solid var(--main-600, #2FA84F);
                    padding: 16px 20px;
                    border-radius: 0 12px 12px 0;
                    margin: 16px 0;
                }
                .policy-bullet-list {
                    padding-left: 20px;
                    margin-bottom: 16px;
                }
                .policy-bullet-list li {
                    margin-bottom: 8px;
                    color: #4A5568;
                    line-height: 1.65;
                }
            `}</style>

            <div className="container container-lg">
                {/* Header Banner */}
                <div className="text-center mb-48 wow fadeInUp" data-wow-duration="0.6s">
                    <span className="text-main-600 fw-semibold text-uppercase mb-12 d-inline-block px-16 py-6 rounded-pill bg-main-50" style={{ letterSpacing: '1px', fontSize: 13 }}>
                        Shipping &amp; Logistics
                    </span>
                    <h2 className="fw-bold mb-16">Shipping &amp; Delivery Policy</h2>
                    <p className="text-gray-600 max-w-700 mx-auto" style={{ fontSize: 16, lineHeight: 1.7 }}>
                        Learn how Healthy Home delivers fresh, pure, cold-pressed oils and natural food products safely to your doorstep.
                    </p>
                </div>

                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="d-flex flex-column gap-32">

                            {/* Section 1: Delivery Coverage & Address Accuracy */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s">
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box">
                                        <i className="ph ph-map-pin" />
                                    </div>
                                    <h4 className="fw-bold mb-0">1. Delivery Coverage &amp; Customer Responsibility</h4>
                                </div>
                                <p className="text-gray-700 mb-16" style={{ lineHeight: 1.8 }}>
                                    Orders will be safely packed and shipped to the delivery address provided by the customer during checkout.
                                </p>
                                <div className="policy-notice-box">
                                    <p className="mb-0 text-gray-800 text-sm">
                                        <strong>Accurate Information Required:</strong> Customers are responsible for providing a complete, accurate delivery address with landmarks and a valid, reachable 10-digit mobile number to avoid delivery delays or failed attempts.
                                    </p>
                                </div>
                            </div>

                            {/* Section 2: Delivery Timelines */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s">
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box">
                                        <i className="ph ph-clock-countdown" />
                                    </div>
                                    <h4 className="fw-bold mb-0">2. Estimated Delivery Timelines</h4>
                                </div>
                                <p className="text-gray-700 mb-16" style={{ lineHeight: 1.8 }}>
                                    Estimated delivery times shown on our website and at checkout are indicative:
                                </p>
                                <ul className="policy-bullet-list">
                                    <li><strong>Dispatch:</strong> Orders are typically processed and handed over to courier partners within 1 to 2 business days.</li>
                                    <li><strong>Transit Time:</strong> Estimated delivery times may vary depending on the destination location, courier service efficiency, regional holidays, extreme weather conditions, or operational disruptions beyond our reasonable control.</li>
                                </ul>
                            </div>

                            {/* Section 3: Shipping Charges & Pricing */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s">
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box">
                                        <i className="ph ph-currency-inr" />
                                    </div>
                                    <h4 className="fw-bold mb-0">3. Shipping Charges &amp; Taxes</h4>
                                </div>
                                <p className="text-gray-700 mb-16" style={{ lineHeight: 1.8 }}>
                                    All product prices on <strong>healthyhomeonline.in</strong> are displayed in Indian Rupees (₹) and are inclusive of applicable taxes.
                                </p>
                                <p className="text-gray-700 mb-0" style={{ lineHeight: 1.8 }}>
                                    Delivery or shipping charges, if applicable based on weight, volume, or delivery zone, are calculated and clearly shown in the cart and checkout before your order is confirmed.
                                </p>
                            </div>

                            {/* Section 4: Damaged or Leaking Parcels */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s">
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box">
                                        <i className="ph ph-package" />
                                    </div>
                                    <h4 className="fw-bold mb-0">4. Parcel Inspection Upon Delivery</h4>
                                </div>
                                <p className="text-gray-700 mb-16" style={{ lineHeight: 1.8 }}>
                                    If the outer package appears severely damaged, tampered with, or leaking at the time of delivery, we encourage customers to take photos/video before accepting and reach out immediately to our support team.
                                </p>
                                <p className="text-gray-700 mb-0" style={{ lineHeight: 1.8 }}>
                                    For detailed eligibility regarding returns or refunds for transit damage, please refer to our <Link to="/refund-policy" className="text-main-600 fw-semibold hover-text-decoration-underline">Cancellation, Return &amp; Refund Policy</Link>.
                                </p>
                            </div>

                            {/* Section 5: Contact Information */}
                            <div className="policy-card p-32 wow fadeInUp" data-wow-duration="0.5s" style={{ background: 'linear-gradient(135deg, #F8FDF9 0%, #FFFFFF 100%)', border: '1px solid #CDE8D5' }}>
                                <div className="d-flex align-items-center gap-16 mb-20">
                                    <div className="policy-icon-box" style={{ background: 'var(--main-600, #2FA84F)', color: '#FFFFFF' }}>
                                        <i className="ph ph-envelope-open" />
                                    </div>
                                    <div>
                                        <h4 className="fw-bold mb-4">5. Delivery Support &amp; Tracking Queries</h4>
                                        <span className="text-sm text-gray-600">Healthy Home &bull; healthyhomeonline.in</span>
                                    </div>
                                </div>
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <p className="text-sm text-gray-500 mb-4">Phone Support</p>
                                        <a href="tel:9449818329" className="text-main-600 fw-semibold text-md">+91 9449818329</a>
                                    </div>
                                    <div className="col-md-4">
                                        <p className="text-sm text-gray-500 mb-4">Official Website</p>
                                        <a href="https://healthyhomeonline.in" target="_blank" rel="noopener noreferrer" className="text-main-600 fw-semibold text-md">healthyhomeonline.in</a>
                                    </div>
                                    <div className="col-md-4">
                                        <p className="text-sm text-gray-500 mb-4">Location</p>
                                        <span className="text-gray-900 fw-semibold text-md">Bengaluru, Karnataka, India</span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ShippingPolicy;
